<template>
  <div class="container">
    <div class="title">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>设施运维</el-breadcrumb-item>
        <el-breadcrumb-item>工程服务</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/facilityManage/formal/technicalReform' }"
          >技术改造</el-breadcrumb-item
        >
        <el-breadcrumb-item>项目详情</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 流程图组件 -->
    <div class="process-flow">
      <div class="title-en">项目流程</div>
      <el-card class="flow-card" shadow="hover">
        <el-steps :active="getActiveStep()" align-center>
          <el-step title="创建项目" icon="el-icon-video-play">
            <template slot="description">
              <div class="step-desc">
                <div>{{ formData.creator || '-' }}</div>
                <div>{{ formatTime(formData.create_time) || '-' }}</div>
              </div>
            </template>
          </el-step>
          <el-step
            title="提交方案"
            :icon="formData.operation_status == '1' ? 'el-icon-loading' : 'el-icon-upload2'"
          >
            <template slot="description">
              <div class="step-desc">
                <div>{{ formData.auditor || '-' }}</div>
                <div>{{ getLastSubmitTime() || '-' }}</div>
              </div>
            </template>
          </el-step>
          <el-step
            title="方案审核"
            :icon="formData.operation_status == '2' ? 'el-icon-loading' : 'el-icon-edit-outline'"
          >
            <template slot="description">
              <div class="step-desc">
                <div>{{ formData.auditor || '-' }}</div>
                <div>
                  {{ getApprovedTime || '-' }}
                </div>
              </div>
            </template>
          </el-step>
          <el-step
            title="确认归档"
            :icon="formData.operation_status == '3' ? 'el-icon-loading' : 'el-icon-folder-checked'"
          >
            <template slot="description">
              <div class="step-desc">
                <div>{{ formData.auditor || '-' }}</div>
                <div>
                  {{ getApprovedTime || '-' }}
                </div>
              </div>
            </template>
          </el-step>
          <el-step title="归档完成" icon="el-icon-check">
            <template slot="description">
              <div class="step-desc">
                <div>{{ formData.auditor || '-' }}</div>
                <div v-if="latestRecord.operation_type === 'archive'">
                  {{ formatTime(latestRecord.operation_time) || '-' }}
                </div>
              </div>
            </template>
          </el-step>
        </el-steps>
      </el-card>
    </div>

    <!-- 基础信息 -->
    <div>
      <div class="title-en">基础信息</div>
      <el-card class="info-card">
        <!-- 详情展示 -->
        <template v-if="!isEditing">
          <el-descriptions :column="3" border>
            <el-descriptions-item label="项目名称">
              {{ formData.project_name || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="项目地点">
              {{ formData.project_location || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="创建人">
              {{ formData.creator || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="创建日期">
              {{ formatTime(formData.create_time) || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="修改日期">
              {{ formatTime(formData.modify_date) || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="项目类型">
              {{ formData.project_type || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="项目状态">
              {{ formData.audit_status || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="客户名称">
              {{ formData.customer_name || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="方案负责人">
              {{ formData.solution_manager || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="项目负责人">
              {{ formData.project_manager || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="附件" :span="24">
              <div v-if="formData.attachments && formData.attachments.length > 0">
                <div
                  v-for="(file, index) in formData.attachments"
                  :key="index"
                  class="attachment-item"
                >
                  <el-link type="primary" @click="handlePreview(file)">{{ file.name }}</el-link>
                </div>
              </div>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="项目描述" :span="24">
              <div style="white-space: pre-line">{{ formData.project_desc || '-' }}</div>
            </el-descriptions-item>
          </el-descriptions>
          <el-row :gutter="20" style="margin-top: 20px">
            <el-col :span="24">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  @click="handleEdit"
                  v-if="!isEditing"
                  :disabled="formData.operation_status == '3' || formData.operation_status == '4'"
                  >编辑</el-button
                >
                <el-button
                  v-if="loginName === formData.auditor"
                  type="success"
                  @click="handleAudit('已通过')"
                  :disabled="
                    formData.operation_status == '1' ||
                    formData.operation_status == '3' ||
                    formData.operation_status == '4'
                  "
                  >通过</el-button
                >
                <el-button
                  v-if="loginName === formData.auditor"
                  type="danger"
                  @click="handleAudit('已驳回')"
                  :disabled="
                    formData.operation_status == '1' ||
                    formData.operation_status == '3' ||
                    formData.operation_status == '4'
                  "
                  >不通过</el-button
                >
              </div>
            </el-col>
          </el-row>
        </template>

        <!-- 编辑表单 -->
        <template v-else>
          <el-form
            ref="rejectedForm"
            :model="formData"
            :rules="rules"
            label-width="120px"
            class="project-form"
          >
            <el-row :gutter="20">
              <el-col :span="6">
                <el-form-item label="项目名称" prop="project_name">
                  <el-input v-model="formData.project_name" placeholder="请输入项目名称"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="项目类型" prop="project_type">
                  <el-select
                    v-model="formData.project_type"
                    placeholder="请选择项目类型"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="item in projectTypeOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="客户名称" prop="customer_name">
                  <el-select
                    v-model="formData.customer_name"
                    placeholder="请选择客户名称"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="item in customerOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="审核人员" prop="auditor">
                  <el-select
                    v-model="formData.auditor"
                    placeholder="请选择审核人员"
                    style="width: 100%"
                  >
                    <el-option label="周德明" value="周德明"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="6">
                <el-form-item label="项目地点" prop="project_location">
                  <el-input
                    v-model="formData.project_location"
                    placeholder="请输入项目地点"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="方案负责人" prop="solution_manager">
                  <el-input
                    v-model="formData.solution_manager"
                    placeholder="请输入方案负责人"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="项目负责人" prop="project_manager">
                  <el-input
                    v-model="formData.project_manager"
                    placeholder="请输入项目负责人"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="项目描述" prop="project_desc">
                  <el-input
                    type="textarea"
                    v-model="formData.project_desc"
                    :rows="4"
                    placeholder="请按以下格式填写：
1.需求简述；
2.客户技术/工勘联系人及电话
3.期望日期"
                  ></el-input>
                  <div class="form-tip">
                    <div class="tip-header">
                      <i class="el-icon-info"></i>
                      <span>格式要求</span>
                    </div>
                    <div class="tip-content">
                      <div class="tip-item">
                        <span class="tip-number">1.</span>
                        <span class="tip-text">需求简述</span>
                      </div>
                      <div class="tip-item">
                        <span class="tip-number">2.</span>
                        <span class="tip-text">客户技术/工勘联系人及电话</span>
                      </div>
                      <div class="tip-item">
                        <span class="tip-number">3.</span>
                        <span class="tip-text">期望日期</span>
                      </div>
                    </div>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="附件" prop="attachments">
                  <el-upload
                    class="upload-demo"
                    action="#"
                    :http-request="handleUpload"
                    :on-preview="handlePreview"
                    :on-remove="handleRemove"
                    :before-remove="beforeRemove"
                    multiple
                    :limit="3"
                    :on-exceed="handleExceed"
                    :file-list="formData.attachments"
                  >
                    <el-button size="small" type="primary">点击上传</el-button>
                    <div slot="tip" class="el-upload__tip">文件大小不超过50MB</div>
                  </el-upload>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="24">
                <div class="action-buttons">
                  <el-button type="primary" @click="handleSubmit">保存</el-button>
                  <el-button @click="handleCancel">取消</el-button>
                </div>
              </el-col>
            </el-row>
          </el-form>
        </template>
      </el-card>
    </div>

    <!-- 方案提交 -->
    <div class="solution-submit">
      <div class="title-en">方案提交</div>
      <el-card class="solution-card">
        <el-form
          ref="solutionForm"
          :model="solutionForm"
          :rules="solutionRules"
          label-width="120px"
        >
          <el-form-item label="方案附件" prop="attachments">
            <el-upload
              class="upload-demo"
              action="#"
              :http-request="handleSolutionUpload"
              :on-preview="handlePreview"
              :on-remove="handleSolutionRemove"
              :before-remove="beforeRemove"
              multiple
              :limit="5"
              :on-exceed="handleExceed"
              :file-list="solutionForm.attachments"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            >
              <el-button
                size="small"
                type="primary"
                :disabled="formData.operation_status == '3' || formData.operation_status == '4'"
              >
                点击上传</el-button
              >
              <div slot="tip" class="el-upload__tip">
                <div>支持格式：PDF、Word、Excel，单个文件不超过50MB</div>
                <div>最多上传5个文件</div>
              </div>
            </el-upload>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              @click="submitSolution"
              :loading="submitting"
              :disabled="formData.operation_status == '3' || formData.operation_status == '4'"
              >提交方案</el-button
            >
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 已提交方案列表 -->
    <div class="solution-list" v-if="solutionList.length">
      <div class="title-en">已提交方案</div>
      <el-card class="solution-list-card">
        <div class="solution-items">
          <el-row :gutter="20">
            <el-col :span="8" v-for="(solution, index) in solutionList" :key="index">
              <el-card class="solution-item" shadow="hover">
                <div class="solution-header">
                  <i class="el-icon-document"></i>
                  <el-tooltip
                    :content="solution.file_name"
                    placement="top"
                    :disabled="solution.file_name.length <= 20"
                  >
                    <span class="file-name">{{
                      solution.file_name.length > 20
                        ? solution.file_name.substring(0, 20) + '...'
                        : solution.file_name
                    }}</span>
                  </el-tooltip>
                </div>
                <div class="solution-info">
                  <div class="info-row">
                    <span class="label">提交人：</span>
                    <span>{{ solution.submitter }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">提交时间：</span>
                    <span>{{ formatTime(solution.submit_time) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">文件大小：</span>
                    <span>{{ (solution.file_size / 1024).toFixed(2) }} KB</span>
                  </div>
                </div>
                <div class="solution-actions">
                  <el-button
                    type="text"
                    class="delete-btn"
                    style="color: #f56c6c"
                    @click="deleteTechnicalSolutionAttachment(solution)"
                    >删除</el-button
                  >
                  <el-button type="text" size="mini" @click="handleDownload(solution)">
                    <i class="el-icon-download"></i> 下载
                  </el-button>
                  <el-button type="text" size="mini" @click="handleFileSaveAs(solution)">
                    <i class="el-icon-folder-add"></i> 另存为
                  </el-button>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 审核记录表格 -->
    <div class="audit-records">
      <div class="title-en">操作步骤</div>
      <el-card class="audit-steps-card">
        <el-collapse v-model="activeSteps">
          <el-collapse-item v-for="(record, index) in auditRecords" :key="index" :name="index">
            <template slot="title">
              <div class="step-header">
                <i :class="getOperationIcon(record.audit_status)" class="operation-icon"></i>
                <span class="step-title">{{ getOperationTitle(record.audit_status) }}</span>
                <span class="step-time">{{ record.created_at }}</span>
                <div
                  class="archive-actions"
                  v-if="record.audit_status === '1' && index == auditRecords.length - 1"
                >
                  <el-button
                    type="success"
                    size="mini"
                    icon="el-icon-folder"
                    @click.stop="handleArchiveConfirm(record)"
                    >归档确认</el-button
                  >
                  <el-button
                    type="danger"
                    size="mini"
                    icon="el-icon-close"
                    @click.stop="handleProjectReject(record)"
                    >项目驳回</el-button
                  >
                </div>
              </div>
            </template>
            <div class="step-content">
              <div class="step-info">
                <div class="info-item">
                  <span class="label">操作人：</span>
                  <span>{{ record.auditor }}</span>
                </div>
                <div class="info-item">
                  <span class="label">操作类型：</span>
                  <el-tag :type="getAuditStatusType(record.audit_status)" size="small">
                    {{ getAuditStatusText(record.audit_status) }}
                  </el-tag>
                </div>
                <div class="info-item">
                  <span class="label">操作说明：</span>
                  <span>{{ record.audit_opinion }}</span>
                </div>
                <div class="info-item" v-if="record.operation_type === 'audit'">
                  <span class="label">审核意见：</span>
                  <span>{{ record.audit_idea || '-' }}</span>
                </div>
                <div class="info-item" v-if="record.operation_type === 'archive'">
                  <span class="label">归档说明：</span>
                  <span>{{ record.remark || '-' }}</span>
                </div>
                <div class="info-item" v-if="record.operation_type === 'reject'">
                  <span class="label">驳回原因：</span>
                  <span>{{ record.remark || '-' }}</span>
                </div>
                <div
                  class="info-item"
                  v-if="record.operation_type !== 'archive' && record.operation_type !== 'reject'"
                >
                  <span class="label">相关附件：</span>
                  <div class="attachments-container">
                    <template v-if="record.operation_type === 'audit'">
                      <div v-if="record.audit_attachments" class="attachments-list">
                        <template v-if="typeof record.audit_attachments === 'string'">
                          <div class="attachment-item">
                            <el-link
                              type="primary"
                              @click="
                                handlePreview({
                                  name: record.filename,
                                  url: record.audit_attachments,
                                })
                              "
                              >{{ record.filename || '-' }}</el-link
                            >
                          </div>
                        </template>
                        <template v-else>
                          <div
                            v-for="(file, index) in record.audit_attachments"
                            :key="index"
                            class="attachment-item"
                          >
                            <el-link type="primary" @click="handlePreview(file)">{{
                              file.name || '-'
                            }}</el-link>
                          </div>
                        </template>
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-else-if="record.operation_type === 'submit'">
                      <div
                        v-if="record.solution_attachments && record.solution_attachments.length > 0"
                        class="attachments-list"
                      >
                        <div
                          v-for="(file, index) in record.solution_attachments"
                          :key="index"
                          class="attachment-item"
                        >
                          <el-link type="primary" @click="handlePreview(file)">{{
                            file.name
                          }}</el-link>
                        </div>
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template v-else>
                      <div
                        v-if="record.attachments && record.attachments.length > 0"
                        class="attachments-list"
                      >
                        <div
                          v-for="(file, index) in record.attachments"
                          :key="index"
                          class="attachment-item"
                        >
                          <el-link type="primary" @click="handlePreview(file)">{{
                            file.name
                          }}</el-link>
                        </div>
                      </div>
                      <span v-else>-</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </el-card>
    </div>

    <!-- 归档确认对话框 -->
    <el-dialog
      title="归档确认"
      :visible.sync="archiveDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="archiveForm" ref="archiveForm" :rules="archiveRules">
        <el-form-item label="归档说明" prop="archiveNote" label-width="100px">
          <el-input
            type="textarea"
            v-model="archiveForm.archiveNote"
            :rows="4"
            placeholder="请输入归档说明"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="archiveDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmArchive">确认</el-button>
      </div>
    </el-dialog>

    <!-- 项目驳回对话框 -->
    <el-dialog
      title="项目驳回"
      :visible.sync="rejectDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="rejectForm" ref="rejectForm" :rules="rejectRules">
        <el-form-item label="驳回原因" prop="rejectReason" label-width="100px">
          <el-input
            type="textarea"
            v-model="rejectForm.rejectReason"
            :rows="4"
            placeholder="请输入驳回原因"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmReject">确认驳回</el-button>
      </div>
    </el-dialog>

    <!-- 审核对话框 -->
    <el-dialog
      title="审核确认"
      :visible.sync="auditDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="auditForm" ref="auditForm" :rules="auditRules">
        <el-form-item label="审核意见" prop="opinion" label-width="100px">
          <el-input
            type="textarea"
            v-model="auditForm.opinion"
            :rows="4"
            placeholder="请输入审核意见"
          ></el-input>
        </el-form-item>
        <el-form-item label="附件" prop="attachments" label-width="100px">
          <el-upload
            class="upload-demo"
            action="#"
            :http-request="handleAuditUpload"
            :on-preview="handlePreview"
            :on-remove="handleAuditAttachmentRemove"
            :before-remove="beforeRemove"
            multiple
            :limit="3"
            :on-exceed="handleExceed"
            :file-list="auditForm.attachments"
          >
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">文件大小不超过50MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="auditDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitAudit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTechnicalReformDetail,
  updateTechnicalReform,
  deleteTechnicalReformAttachment,
  auditTechnicalReform,
  getReformLogsById,
  submitProjectSolution,
  getTechnical_solution,
  deleteTechnicalSolutionAttachment,
  technicalReformArchive,
  technicalReformReject,
} from '@/api/skill';
import axios from 'axios';
export default {
  name: 'TechnicalReformDetail',
  data() {
    return {
      // 归档确认相关
      archiveDialogVisible: false,
      archiveForm: {
        archiveNote: '',
      },
      archiveRules: {
        archiveNote: [{ required: true, message: '请输入归档说明', trigger: 'blur' }],
      },
      // 项目驳回相关
      rejectDialogVisible: false,
      rejectForm: {
        rejectReason: '',
      },
      rejectRules: {
        rejectReason: [{ required: true, message: '请输入驳回原因', trigger: 'blur' }],
      },
      solutionList: [], // 已提交的方案列表
      submitting: false,
      solutionForm: {
        attachments: [],
      },
      solutionRules: {
        attachments: [{ required: true, message: '请上传方案附件', trigger: 'change' }],
      },
      activeStep: 0,
      maxFileSize: 10,
      loading: false,
      latestRecord: {},
      activeSteps: [], // 当前展开的步骤
      isEditing: false, // 是否处于编辑状态
      tempFormData: null, // 临时存储编辑前的表单数据
      formData: {
        project_name: '',
        project_type: '',
        customer_name: '',
        project_location: '',
        plan_date: '',
        list_date: '',
        project_desc: '',
        attachments: [],
        auditStatus: 'pending',
        auditOpinion: '',
        auditor: '',
        auditTime: '',
        creator: '',
        create_time: '',
        solution_manager: '',
        project_manager: '',
      },
      auditDialogVisible: false,
      auditAction: '',
      auditForm: {
        opinion: '',
        attachments: [],
      },
      auditRecords: [],
      recordStatus: null,
      projectTypeOptions: [
        { value: 'framework', label: '框架' },
        { value: 'sporadic', label: '零星' },
        { value: 'special', label: '专项' },
      ],
      customerOptions: [
        { value: 'tencent', label: '腾讯' },
        { value: 'alibaba', label: '阿里' },
        { value: 'qinhuai', label: '秦淮' },
        { value: 'bytedance', label: '字节' },
        { value: 'kuaishou', label: '快手' },
        { value: 'transsion', label: '传音' },
        { value: 'other', label: '其它' },
      ],
      loginName: '',
      rules: {
        project_name: [
          { required: true, message: '请输入项目名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' },
        ],
        project_type: [{ required: true, message: '请选择项目类型', trigger: 'change' }],
        customer_name: [{ required: true, message: '请选择客户名称', trigger: 'change' }],
        project_location: [{ required: true, message: '请输入项目地点', trigger: 'blur' }],
        solution_manager: [{ required: true, message: '请选择方案负责人', trigger: 'change' }],
        project_manager: [{ required: true, message: '请选择项目负责人', trigger: 'change' }],
        plan_date: [{ required: true, message: '请选择方案日期', trigger: 'change' }],
        list_date: [{ required: true, message: '请选择清单日期', trigger: 'change' }],
        project_desc: [{ required: true, message: '请输入项目描述', trigger: 'blur' }],
        auditor: [{ required: true, message: '请选择审核人员', trigger: 'change' }],
      },
      auditRules: {
        opinion: [{ required: true, message: '请输入审核意见', trigger: 'blur' }],
      },
    };
  },
  computed: {
    canSubmitSolution() {
      // 只有在审核通过状态下才能提交方案
      return this.formData.audit_status === '已通过';
    },
    getApprovedTime() {
      if (!this.auditRecords || this.auditRecords.length === 0) return null;
      const approvedRecord = this.auditRecords.find((record) => record.audit_status === '1');
      return approvedRecord ? this.formatTime(approvedRecord.operation_time) : null;
    },
  },
  created() {
    // 获取路由参数中的项目ID
    const projectId = this.$route.query.id;
    this.getProjectDetail(projectId);
    this.getReformLogsById(projectId);
    this.getTechnical_solutions(projectId);
  },
  mounted() {
    this.loginName = localStorage.getItem('UserId');
  },
  methods: {
    handleArchiveConfirm(record) {
      this.archiveDialogVisible = true;
      this.currentRecord = record;
    },
    handleProjectReject(record) {
      this.rejectDialogVisible = true;
      this.currentRecord = record;
    },
    async confirmArchive() {
      try {
        await this.$refs.archiveForm.validate();
        const projectId = this.$route.query.id;
        const formData = new FormData();
        formData.append('archive_note', this.archiveForm.archiveNote);
        formData.append('operator', this.formData.auditor);
        await technicalReformArchive(projectId, formData);
        this.$message.success('归档成功');
        this.archiveDialogVisible = false;
        this.archiveForm.archiveNote = '';
        this.getProjectDetail(this.$route.query.id);
        this.getReformLogsById(this.$route.query.id);
        this.getTechnical_solutions(this.$route.query.id);
      } catch (error) {
        console.error('归档失败:', error);
        this.$message.error('归档失败');
      }
    },
    async handleFileSaveAs(file) {
      try {
        const fileName = file.file_name;

        // 使用 File System Access API 保存文件
        if (window.showSaveFilePicker) {
          const options = {
            suggestedName: fileName,
            types: [
              {
                description: '所有文件',
              },
            ],
          };

          // 通过后端代理获取文件内容
          const response = await axios({
            url: '/api/v1/ops/download',
            method: 'GET',
            params: {
              url: file.file_path,
              filename: fileName,
            },
            responseType: 'blob',
          });

          // 保存文件
          const handle = await window.showSaveFilePicker(options);
          const writable = await handle.createWritable();
          await writable.write(response.data);
          await writable.close();

          this.$message.success('文件已成功保存到指定目录！');
        } else {
          // 降级方案：使用传统下载方式
          const link = document.createElement('a');
          link.href = `/api/download?url=${encodeURIComponent(
            file.file_path
          )}&filename=${encodeURIComponent(fileName)}`;
          link.download = fileName;

          // 设置下载属性
          link.setAttribute('download', fileName);
          link.setAttribute('target', '_blank');

          // 添加到文档中并触发点击
          document.body.appendChild(link);
          link.click();

          // 清理DOM
          setTimeout(() => {
            document.body.removeChild(link);
          }, 100);

          this.$message.success('文件已开始下载！');
        }
      } catch (error) {
        console.error('文件保存失败:', error);
        let errorMessage = '文件保存失败';

        if (error.name === 'AbortError') {
          errorMessage = '用户取消了保存操作';
        } else if (error.name === 'SecurityError') {
          errorMessage = '由于安全限制，无法保存文件';
        } else if (error.name === 'NotAllowedError') {
          errorMessage = '没有保存文件的权限';
        } else if (error.message.includes('Network Error')) {
          errorMessage = '网络错误，请检查网络连接';
        }

        this.$message.error(errorMessage);
      }
    },
    async confirmReject() {
      try {
        await this.$refs.rejectForm.validate();
        const projectId = this.$route.query.id;
        const formData = new FormData();
        formData.append('reject_reason', this.rejectForm.rejectReason);
        formData.append('operator', this.formData.auditor);
        await technicalReformReject(projectId, formData);
        this.$message.success('驳回成功');
        this.rejectDialogVisible = false;
        this.rejectForm.rejectReason = '';
        this.getProjectDetail(this.$route.query.id);
        this.getReformLogsById(this.$route.query.id);
        this.getTechnical_solutions(this.$route.query.id);
      } catch (error) {
        console.error('驳回失败:', error);
        this.$message.error('驳回失败');
      }
    },
    // 下载方案文件

    getTechnical_solutions(id) {
      getTechnical_solution(id).then((response) => {
        if (response.data.code === 200) {
          this.solutionList = response.data.data.data || [];
        } else {
          this.$message.error('获取方案列表失败');
        }
      });
    },
    // 处理方案文件上传
    handleSolutionUpload(options) {
      const { file } = options;
      // 校验文件大小
      const maxSize = 50 * 1024 * 1024;
      const isLtMaxSize = file.size < maxSize;
      if (!isLtMaxSize) {
        this.$message.error('上传文件大小不能超过50MB');
        return;
      }
      // 将文件添加到方案附件列表
      this.solutionForm.attachments.push({
        name: file.name,
        raw: file,
      });
      options.onSuccess();
    },

    // 移除方案文件
    handleSolutionRemove(file) {
      const index = this.solutionForm.attachments.indexOf(file);
      if (index !== -1) {
        this.solutionForm.attachments.splice(index, 1);
      }
    },

    // 提交方案
    submitSolution() {
      this.$refs.solutionForm.validate(async (valid) => {
        if (valid) {
          try {
            this.submitting = true;
            // 构建FormData
            const formData = new FormData();
            formData.append('submitter', localStorage.getItem('UserId'));

            // 添加方案附件
            if (this.solutionForm.attachments && this.solutionForm.attachments.length > 0) {
              this.solutionForm.attachments.forEach((file) => {
                if (file?.raw) {
                  formData.append('solution_files', file.raw);
                }
              });
            }
            const projectId = this.$route.query.id;

            const response = await submitProjectSolution(projectId, formData);
            this.$message.success('方案提交成功');
            this.getProjectDetail(this.$route.query.id);
            this.getReformLogsById(this.$route.query.id);
            this.getTechnical_solutions(this.$route.query.id);
            // 清空表单
            this.solutionForm.attachments = [];
          } catch (error) {
            console.error('方案提交失败:', error);
            this.$message.error(`方案提交失败: ${error.message || '未知错误'}`);
          } finally {
            this.submitting = false;
          }
        }
      });
    },

    // 处理编辑按钮点击
    handleEdit() {
      this.isEditing = true;
      // 保存编辑前的数据，用于取消时恢复
      this.tempFormData = JSON.parse(JSON.stringify(this.formData));
    },

    // 处理保存按钮点击
    handleSave() {
      this.$refs.editForm.validate(async (valid) => {
        if (valid) {
          try {
            this.loading = true;
            // 调用更新接口
            const response = await updateTechnicalReform({
              ...this.formData,
              id: this.$route.query.id,
            });
            if (response.data.code === 200) {
              this.$message.success('保存成功');
              this.isEditing = false;
              // 刷新数据
              this.getProjectDetail(this.$route.query.id);
              this.getReformLogsById(this.$route.query.id);
              this.getTechnical_solutions(this.$route.query.id);
            } else {
              this.$message.error(response.data.msg || '保存失败');
            }
          } catch (error) {
            console.error('保存失败:', error);
            this.$message.error('保存失败');
          } finally {
            this.loading = false;
          }
        }
      });
    },

    // 处理取消按钮点击
    handleCancel() {
      // 恢复编辑前的数据
      this.formData = JSON.parse(JSON.stringify(this.tempFormData));
      this.isEditing = false;
      this.tempFormData = null;
    },

    getReformLogsById(id) {
      getReformLogsById(id).then((res) => {
        if (res.data.code === 200) {
          // 处理操作记录数据
          this.oldRecords = res.data.data.list || [];
          this.auditRecords = (res.data.data.list || []).map((record) => {
            // 解析before_content和after_content
            const beforeContent = record.before_content ? JSON.parse(record.before_content) : {};
            const afterContent = record.after_content ? JSON.parse(record.after_content) : {};
            // 处理附件数据
            const attachments = record.attachments || [];
            const auditAttachments = record.audit_attachments || [];

            // 构建审核记录对象
            return {
              id: record.id,
              audit_status: record.audit_status,
              auditor: record.operator,
              audit_opinion: record.operation_content || '无',
              audit_idea: afterContent.audit_opinion || '无',
              operation_type: record.operation_type,
              created_at: new Date(record.operation_time).toLocaleString(),
              attachments: this.parseAttachments(attachments),
              audit_attachments: this.parseAttachments(auditAttachments),
              solution_attachments: this.parseAttachments(record.solution_attachments || []),
              before_content: beforeContent,
              after_content: afterContent,
              operation_time: record.operation_time,
              remark: record.remark,
            };
          });
          this.auditRecords = this.auditRecords.reverse();
        }
      });
    },

    // 获取最后一个提交方案的时间
    getLastSubmitTime() {
      if (!this.oldRecords || this.oldRecords.length === 0) return null;

      const submitRecords = this.oldRecords.filter((record) => record.operation_type === 'submit');
      if (submitRecords.length === 0) return null;
      const lastSubmitRecord = submitRecords[0];
      return this.formatTime(lastSubmitRecord.operation_time);
    },

    // 解析附件数据为统一格式
    parseAttachments(attachments) {
      if (!attachments || !Array.isArray(attachments)) return [];

      return attachments.map((attachment) => ({
        file_name: attachment.file_name,
        file_path: attachment.file_path.trim().replace(/[`\s]/g, ''),
        name: attachment.file_name,
        url: attachment.file_path.trim().replace(/[`\s]/g, ''),
      }));
    },
    // 获取操作图标
    getOperationIcon(audit_status) {
      switch (audit_status) {
        case 0:
          return 'el-icon-edit';
        case 1:
          return 'el-icon-check';
        case 2:
          return 'el-icon-close';
        case 3:
          return 'el-icon-warning';
        default:
          return 'el-icon-info';
      }
    },

    // 获取操作标题
    getOperationTitle(audit_status) {
      switch (audit_status) {
        case 0:
          return '创建项目';
        case 1:
          return '审核通过';
        case 2:
          return '审核驳回';
        case 3:
          return '提交方案';
        case 4:
          return '归档确认';
        case 5:
          return '项目驳回';
        case 6:
          return '更新项目';
        default:
          return '未知操作';
      }
    },

    // 获取审核状态类型
    getAuditStatusType(audit_status) {
      switch (audit_status) {
        case 0:
          return 'info';
        case 1:
          return 'success';
        case 2:
          return 'danger';
        case 3:
          return 'warning';
        case 4:
          return 'info';
        case 5:
          return 'danger';
        case 6:
          return 'warning';
        default:
          return 'info';
      }
    },

    // 获取审核状态文本
    getAuditStatusText(audit_status) {
      switch (audit_status) {
        case 0:
          return '待审核';
        case 1:
          return '已通过';
        case 2:
          return '已驳回';
        case 3:
          return '提交方案';
        case 4:
          return '已归档';
        case 5:
          return '已驳回';
        case 6:
          return '已更新';
        default:
          return '未知状态';
      }
    },

    getProjectTypeName(type) {
      const option = this.projectTypeOptions.find((item) => item.value === type);
      return option ? option.label : '';
    },
    getCustomerName(name) {
      const option = this.customerOptions.find((item) => item.value === name);
      return option ? option.label : '';
    },
    // 格式化时间方法
    formatTime(time) {
      if (!time) return '';
      return new Date(time).toLocaleString();
    },
    // 获取当前活动步骤
    getActiveStep() {
      if (!this.formData.operation_status) {
        return 0; // 项目创建
      }
      if (this.auditRecords && this.auditRecords.length !== 0) {
        const latestRecord = this.auditRecords[this.auditRecords.length - 1]; // 因为记录是倒序排列的
        this.latestRecord = latestRecord;
        this.recordStatus = latestRecord.audit_status;
        console.log('latestRecord', this.recordStatus);
      }

      const operation_status = this.formData.operation_status;
      if (operation_status === '1') {
        return 2;
      } else if (operation_status === '2') {
        return 3;
      } else if (operation_status === '3') {
        return 4;
      } else if (operation_status === '4') {
        return 5;
      } else {
        return 1;
      }
    },

    handleBack() {
      this.$router.go(-1);
    },
    // 获取项目详情
    async getProjectDetail(id) {
      try {
        const response = await getTechnicalReformDetail(id);
        if (response.data.code === 200) {
          const data = response.data.data;
          // 更新表单数据
          this.formData = {
            ...data.data,
            attachments:
              data.data.attachments?.map((item) => ({
                ...item,
                name: item.file_name,
                url: item.file_path?.trim(),
              })) || [],
          };
        } else {
          this.$message.error(response.message || '获取项目详情失败');
        }
      } catch (error) {
        console.error('获取项目详情失败:', error);
        this.$message.error('获取项目详情失败');
      }
    },
    // 处理审核操作
    handleAudit(action) {
      this.auditAction = action;
      this.auditDialogVisible = true;
    },
    // 处理审核附件上传
    handleAuditUpload(options) {
      const { file } = options;
      // 校验文件大小
      const isLtMaxSize = file.size / 1024 / 1024 < 50;
      if (!isLtMaxSize) {
        this.$message.error(`上传文件大小不能超过 ${this.maxFileSize}MB!`);
        return;
      }
      // 将文件添加到审核附件列表
      this.auditForm.attachments.push({
        name: file.name,
        raw: file,
      });
      options.onSuccess();
    },

    // 处理审核附件删除
    handleAuditAttachmentRemove(file) {
      this.auditForm.attachments = this.auditForm.attachments.filter(
        (item) => item.name !== file.name
      );
    },

    // 提交审核
    submitAudit() {
      this.$refs.auditForm.validate(async (valid) => {
        if (valid) {
          try {
            this.loading = true;
            // 构建FormData
            const formData = new FormData();
            formData.append('audit_status', this.auditAction);
            formData.append('audit_opinion', this.auditForm.opinion);
            formData.append('auditor', this.formData.auditor || '匿名用户');

            // 添加审核附件
            if (this.auditForm.attachments && this.auditForm.attachments.length > 0) {
              this.auditForm.attachments.forEach((file) => {
                if (file?.raw) {
                  formData.append('audit_files', file.raw);
                }
              });
            }

            // 调用审核接口
            const projectId = this.$route.query.id;
            // 发送请求
            // const response = await updateTechnicalReform(projectId, formData);
            // TODO: 替换为实际的审核API调用
            const response = await auditTechnicalReform(projectId, formData);

            this.$message.success('审核提交成功');
            this.auditDialogVisible = false;
            this.auditForm.opinion = '';
            this.auditForm.attachments = [];
            // 刷新项目详情
            this.getProjectDetail(projectId);
            this.getReformLogsById(projectId);
            this.getTechnical_solutions(projectId);
          } catch (error) {
            console.error('审核提交失败:', error);
            this.$message.error(`审核提交失败: ${error.message || '未知错误'}`);
          } finally {
            this.loading = false;
          }
        }
      });
    },
    // 处理提交操作
    handleSubmit() {
      // 获取当前激活的表单ref

      this.$refs.rejectedForm.validate(async (valid) => {
        if (valid) {
          try {
            this.loading = true;
            // 构建FormData
            const formData = new FormData();
            formData.append('project_name', this.formData.project_name);
            formData.append('project_type', this.formData.project_type);
            formData.append('customer_name', this.formData.customer_name);
            formData.append('project_location', this.formData.project_location);
            formData.append('project_desc', this.formData.project_desc);
            formData.append('solution_manager', this.formData.solution_manager);
            formData.append('project_manager', this.formData.project_manager);
            formData.append('auditor', localStorage.getItem('UserId') || '匿名用户');
            formData.append('audit_status', '提交方案');

            // 添加文件到FormData
            if (this.formData.attachments && this.formData.attachments.length > 0) {
              formData.append('filename', this.formData.attachments[0].name);
              console.log(this.formData);
              if (this.formData.attachments.length > 0) {
                this.formData.attachments.forEach((file) => {
                  if (file?.raw) {
                    formData.append('files', file?.raw);
                  }
                });
              }
            }

            // 调用更新接口
            const projectId = this.$route.query.id;
            const response = await updateTechnicalReform(projectId, formData);

            if (response.data.code === 200) {
              this.$message.success('保存成功');
              this.isEditing = false;

              // 刷新项目详情
              this.getProjectDetail(projectId);
              this.getReformLogsById(projectId);
              this.getTechnical_solutions(projectId);
            } else {
              throw new Error(response.message || '保存失败');
            }
          } catch (error) {
            console.error('保存失败:', error);
            this.$message.error(`保存失败: ${error.message || '未知错误'}`);
          } finally {
            this.loading = false;
          }
        } else {
          return false;
        }
      });
    },
    // 获取审核状态类型
    getAuditStatusType(status) {
      const types = {
        1: 'success', // 通过
        2: 'danger', // 驳回
        0: 'warning', // 待审核
        3: 'primary', // 提交方案
        4: 'success', // 归档确认
        5: 'danger', // 项目驳回
        6: 'warning', // 更新项目
      };
      return types[status] || 'info';
    },
    // 获取审核状态文本
    getAuditStatusText(status) {
      const texts = {
        1: '已通过',
        2: '已驳回',
        0: '待审核',
        3: '提交方案',
        4: '已归档',
        5: '已驳回',
        6: '已更新',
      };
      return texts[status] || '-';
    },
    // 获取操作图标
    getOperationIcon(status) {
      const icons = {
        1: 'el-icon-check',
        2: 'el-icon-close',
        0: 'el-icon-time',
        3: 'el-icon-upload2',
        4: 'el-icon-check',
        5: 'el-icon-close',
        6: 'el-icon-edit',
      };
      return icons[status] || 'el-icon-info';
    },
    // 获取操作标题
    getOperationTitle(status) {
      const titles = {
        0: '创建项目',
        1: '方案审核',
        2: '审核驳回',
        3: '提交方案',
        4: '归档完成',
        5: '项目驳回',
        6: '更新项目',
      };
      return titles[status] || '其他操作';
    },
    // 处理附件预览
    handleAttachmentPreview(record) {
      if (record.handleAttachmentPreview && record.handleAttachmentPreview.length > 0) {
        const attachment = record.handleAttachmentPreview[0];
        window.open(attachment.file_path);
      }
    },
    handleUpload(options) {
      const { file } = options;

      // 校验文件大小
      const isLtMaxSize = file.size / 1024 / 1024 < 50;
      if (!isLtMaxSize) {
        this.$message.error(`上传文件大小不能超过 ${this.maxFileSize}MB!`);
        return;
      }

      // 将文件添加到fileList
      this.formData.attachments.push({
        name: file.name,
        raw: file,
      });
      console.log(this.formData.attachments);
      // 触发上传成功回调
      options.onSuccess();
    },
    // 文件上传相关方法
    // handleUpload({ file }) {
    //   // 这里应该调用API上传文件
    //   const attachment = {
    //     name: file.name, // 使用name字段以符合el-upload的file-list要求
    //     file_name: file.name,
    //     file_size: file.size,
    //     file_path: file.url || URL.createObjectURL(file),
    //     created_at: new Date().toISOString(),
    //   };
    //   this.formData.attachments = [attachment]; // 由于limit=1，直接替换整个数组
    // },
    async handleDownload(solution) {
      try {
        const fileName = solution.file_name;
        console.log('Downloading file:', fileName);

        // 使用 axios 获取文件
        const response = await axios({
          url: '/api/v1/ops/download',
          method: 'GET',
          params: {
            url: solution.file_path,
            filename: fileName,
          },
          responseType: 'blob',
        });

        // 创建 Blob URL
        const blobUrl = window.URL.createObjectURL(response.data);

        // 创建下载链接
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        this.$message.success(`文件 ${fileName} 开始下载`);
      } catch (error) {
        console.error('文件下载失败:', error);
        this.$message.error('文件下载失败，请重试');
      }
    },
    async handlePreview(solution) {
      try {
        const fileName = solution.file_name;
        console.log('Downloading file:', fileName);

        // 使用 axios 获取文件
        const response = await axios({
          url: '/api/v1/ops/download',
          method: 'GET',
          params: {
            url: solution.file_path,
            filename: fileName,
          },
          responseType: 'blob',
        });

        // 创建 Blob URL
        const blobUrl = window.URL.createObjectURL(response.data);

        // 创建下载链接
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        this.$message.success(`文件 ${fileName} 开始下载`);
      } catch (error) {
        console.error('文件下载失败:', error);
        this.$message.error('文件下载失败，请重试');
      }
    },
    async handleRemove(file) {
      try {
        await deleteTechnicalReformAttachment(file.id);
        this.formData.attachments = this.formData.attachments.filter((item) => item.id !== file.id);
        this.$message.success('删除附件成功');
      } catch (error) {
        console.error('删除附件失败:', error);
        this.$message.error('删除附件失败');
        return false;
      }
    },
    async deleteTechnicalSolutionAttachment(file) {
      try {
        const confirmResult = await this.$confirm('确认要删除该方案附件吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
        if (confirmResult === 'confirm') {
          await deleteTechnicalSolutionAttachment(file.id);
          this.getTechnical_solutions(this.$route.query.id);
          this.$message.success('删除方案附件成功');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除附件失败:', error);
          this.$message.error('删除方案附件失败');
        }
        return false;
      }
    },

    beforeRemove(file) {
      return this.$confirm(`确定移除 ${file.file_name || file.name}？`);
    },
    handleExceed(files, fileList) {
      this.$message.warning(
        `当前限制选择 3 个文件，本次选择了 ${files.length} 个文件，共选择了 ${
          files.length + fileList.length
        } 个文件`
      );
    },
  },
};
</script>

<style lang="less" scoped>
.solution-list {
  margin-bottom: 20px;
}

.solution-list-card {
  margin-top: 10px;
}

.solution-items {
  padding: 10px;
}

.solution-item {
  margin-bottom: 20px;
  transition: all 0.3s;
}

.solution-item:hover {
  transform: translateY(-5px);
}

.solution-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.solution-header i {
  font-size: 20px;
  color: #409eff;
  margin-right: 10px;
}

.solution-header .file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.solution-info {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
}

.info-row .label {
  width: 70px;
  color: #909399;
}

.solution-actions {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ebeef5;
  padding-top: 10px;
  margin-top: 10px;
}

.solution-actions .el-button {
  padding: 0;
  margin-left: 15px;
}

.solution-actions .el-button:first-child {
  margin-left: 0;
}
.rejected-form {
  margin-top: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.rejected-form .title-en {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

.rejected-form .project-form {
  background-color: #fff;
  padding: 30px 30px 20px 10px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}
.container {
  padding: 20px;
}

.title {
  margin-bottom: 20px;
}

.title-en {
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0;
  padding-left: 10px;
  border-left: 3px solid #219aff;
  color: #333;
}

.flow-card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.custom-steps {
  padding: 20px 0;
}

.custom-steps :deep(.el-step__head.is-process) {
  color: #219aff;
  border-color: #219aff;
}

.custom-steps :deep(.el-step__title.is-process) {
  color: #219aff;
  font-weight: 600;
}

.custom-steps :deep(.el-step__head.is-success) {
  color: #219aff;
  border-color: #219aff;
}

.custom-steps :deep(.el-step__title.is-success) {
  color: #219aff;
}

.custom-steps :deep(.el-step__head .el-step__icon) {
  width: 36px;
  height: 36px;
  font-size: 18px;
}

.custom-steps :deep(.el-step__head.is-process .el-step__icon) {
  background: rgba(33, 154, 255, 0.1);
}

.custom-steps :deep(.el-step__line) {
  background-color: #e8f4ff;
}

// .step-desc {
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   gap: 4px;
//   color: #909399;
//   font-size: 13px;
//   margin-top: 8px;
// }

// .step-desc span {
//   display: block;
//   text-align: center;
//   line-height: 1.4;
// }

.info-card {
  margin-top: 20px;
}

.project-form {
  padding: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.solution-submit {
  margin-top: 20px;

  .solution-card {
    margin-top: 10px;

    .el-upload__tip {
      color: #909399;
      font-size: 12px;
      line-height: 1.5;
    }
  }
}

.audit-records {
  margin-top: 20px;

  .audit-steps-card {
    .step-header {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: space-between;

      .operation-icon {
        font-size: 18px;
        &.el-icon-check {
          color: #67c23a;
        }

        .archive-actions {
          display: flex;
          gap: 8px;
          margin-left: auto;

          .el-button {
            padding: 6px 12px;
            &.el-button--mini {
              font-size: 12px;
            }
            .el-icon-folder,
            .el-icon-close {
              margin-right: 4px;
            }
          }
        }
        &.el-icon-close {
          color: #f56c6c;
        }
        &.el-icon-time {
          color: #e6a23c;
        }
        &.el-icon-info {
          color: #909399;
        }
        &.el-icon-upload2 {
          color: #409eff;
        }
        &.el-icon-edit {
          color: #409eff;
        }
      }

      .step-title {
        font-weight: 500;
        color: #303133;
      }

      .step-time {
        margin-left: auto;
        color: #909399;
        font-size: 13px;
      }
    }

    .step-content {
      padding: 16px;
      background-color: #f8f9fa;
      border-radius: 4px;
      margin-top: 12px;

      .step-info {
        .info-item {
          margin-bottom: 12px;
          line-height: 24px;
          display: flex;
          align-items: flex-start;

          &:last-child {
            margin-bottom: 0;
          }

          .label {
            color: #606266;
            margin-right: 8px;
            min-width: 80px;
          }

          .attachments-container {
            flex: 1;
          }

          .attachments-list {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }

          .attachment-item {
            margin-right: 10px;
            margin-bottom: 5px;
          }
        }
      }
    }
  }
}

/deep/ .el-collapse-item__header {
  padding: 12px 16px;
  &:hover {
    background-color: #f5f7fa;
  }
}

/deep/ .el-collapse-item__content {
  padding: 0 16px 16px;
}

.audit-table-card {
  margin-top: 10px;
  .el-table {
    margin: 10px 0;
  }
}
::v-deep .el-descriptions-item__label.is-bordered-label {
  background-color: #f8f9fa;
  color: #606266;
}
.form-tip {
  margin-top: 10px;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: #fafafa;
}
.tip-header {
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #606266;
}
.tip-header i {
  margin-right: 6px;
  color: #409eff;
}
.tip-content {
  padding: 12px;
}
.tip-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.4;
}
.tip-item:last-child {
  margin-bottom: 0;
}
.tip-number {
  margin-right: 6px;
  color: #409eff;
  font-weight: bold;
}
.tip-text {
  flex: 1;
}
</style>